import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { RoccoLink } from '../../../../components/link/RoccoLink';
import type { FooterLinkGroup } from './types';

interface AccordionLinksSectionProps {
    homeownersLinks: FooterLinkGroup[];
    businessLinks: FooterLinkGroup[];
    supportLinks: FooterLinkGroup[];
    /** Which accordion section is open on first render (e.g. `support` for US legal links). */
    defaultOpenSection?: string | null;
    onNavClick?: (link: string) => void;
}

interface HorizontalAccordionItemProps {
    title: string;
    linkGroups: FooterLinkGroup[];
    onNavClick?: (link: string) => void;
    isOpen: boolean;
    onToggle: () => void;
}

/**
 * Horizontal accordion item - title on left, links on right, icon far right.
 * Maintains accessibility with proper ARIA attributes.
 */
const HorizontalAccordionItem: React.FC<HorizontalAccordionItemProps> = ({
    title,
    linkGroups,
    onNavClick,
    isOpen,
    onToggle,
}) => {
    const itemId = title.toLowerCase().replace(/\s+/g, '-');

    const handleLinkClick = (e: React.MouseEvent, link: string) => {
        e.stopPropagation(); // Prevent accordion toggle when clicking links
        onNavClick?.(link);
    };

    return (
        <div className="border-t border-white/20">
            <div
                className="flex cursor-pointer items-start py-8"
                onClick={onToggle}
            >
                {/* Title - fixed width on left, stays at top */}
                <button
                    type="button"
                    id={`${itemId}-trigger`}
                    aria-expanded={isOpen}
                    aria-controls={`${itemId}-content`}
                    className="w-auto shrink-0 text-left text-[2.5rem] font-normal leading-[1.49] tracking-[-1.06px] text-white transition-colors hover:text-white/80 md:text-[3.3125rem]"
                >
                    {title}
                </button>

                {/* Content - expandable, aligned to the right */}
                <div
                    id={`${itemId}-content`}
                    role="region"
                    aria-labelledby={`${itemId}-trigger`}
                    className={`flex-1 overflow-hidden transition-all duration-300 ease-out ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                    <div className="mr-50 mt-8 flex justify-end gap-24">
                        {linkGroups.map((group) => (
                            <div key={group.title} className="min-w-[10rem]">
                                <h4 className="mb-3 text-sm font-normal uppercase tracking-wider text-gray-400">
                                    {group.title}
                                </h4>
                                <ul className="flex flex-col gap-2">
                                    {group.links.map((link) => (
                                        <li key={link.to}>
                                            <RoccoLink
                                                to={link.to}
                                                className="inline-flex items-center gap-2 text-base text-white transition-colors hover:text-white/80"
                                                isNonRemixRoute={
                                                    link.isNonRemixRoute
                                                }
                                                newTab={link.newTab}
                                                onClick={(e) =>
                                                    handleLinkClick(e, link.to)
                                                }
                                            >
                                                {link.title}
                                                {link.comingSoon && (
                                                    <span className="text-xs text-white/50">
                                                        - coming soon
                                                    </span>
                                                )}
                                            </RoccoLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Icon - far right, stays at top, decorative (parent div handles click interaction) */}
                <span
                    role="presentation"
                    aria-hidden="true"
                    className="ml-auto mt-2 shrink-0 p-1 text-white transition-colors hover:text-white/80"
                >
                    {isOpen ? (
                        <Minus className="h-6 w-6" />
                    ) : (
                        <Plus className="h-6 w-6" />
                    )}
                </span>
            </div>
        </div>
    );
};

/**
 * Accordion links section for the new footer design.
 * Horizontal layout: title on left, links expand to the right, icon on far right.
 * Contains 3 sections: For Homeowners, For Businesses, Support.
 */
export const AccordionLinksSection: React.FC<AccordionLinksSectionProps> = ({
    homeownersLinks,
    businessLinks,
    supportLinks,
    defaultOpenSection = 'homeowners',
    onNavClick,
}) => {
    const [openSection, setOpenSection] = useState<string | null>(
        defaultOpenSection,
    );

    const handleToggle = (section: string) => {
        setOpenSection(openSection === section ? null : section);
    };

    return (
        <section className="bg-[#212121] px-12 pb-8 pt-4">
            <div>
                <HorizontalAccordionItem
                    title="For Homeowners"
                    linkGroups={homeownersLinks}
                    onNavClick={onNavClick}
                    isOpen={openSection === 'homeowners'}
                    onToggle={() => handleToggle('homeowners')}
                />
                <HorizontalAccordionItem
                    title="For Businesses"
                    linkGroups={businessLinks}
                    onNavClick={onNavClick}
                    isOpen={openSection === 'businesses'}
                    onToggle={() => handleToggle('businesses')}
                />
                <HorizontalAccordionItem
                    title="Support"
                    linkGroups={supportLinks}
                    onNavClick={onNavClick}
                    isOpen={openSection === 'support'}
                    onToggle={() => handleToggle('support')}
                />

                {/* Bottom border */}
                <div className="border-t border-white/20" />
            </div>
        </section>
    );
};
